import React, { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";

import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import PageHeaderRow from "../shared/page-header-row";
import Loader from "../loader";
import TiersTable from "./tiers-table";
import ServiceFeesTable from "./service-fees-table";
import SalesRepRatesTable from "./sales-rep-rates-table";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const CommissionsContent = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [serviceFeeBrackets, setServiceFeeBrackets] = useState([]);
  const [salesReps, setSalesReps] = useState([]);

  useEffect(() => {
    document.title = "Commissions | evoJets HQ";
  }, []);

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get("/api/commissions/");

      setTiers(data.tiers);
      setServiceFeeBrackets(data.serviceFeeBrackets);
      setSalesReps(data.salesReps);
    } catch (error) {
      Sentry.captureMessage(
        `Commissions load failed: ${error?.response?.status ?? "network"}`,
        "error"
      );
      toast.error(
        getApiErrorMessage(error, "Failed to load commission settings."),
        toastOptions
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    getData();
  }, [getData]);

  const request = useCallback(
    async (method, url, body, successMessage) => {
      try {
        setIsSaving(true);
        const token = await getAccessTokenSilently();
        const { data } = await ai.auth(token)[method](url, body);
        toast.success(successMessage, toastOptions);
        return data;
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to save changes."), toastOptions);

        if (error?.response?.status === 409) {
          setEditingKey(null);
          getData();
        }

        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [getAccessTokenSilently, getData]
  );

  const handleTierUpdate = useCallback(
    async (tier, values) => {
      const data = await request("put", `/api/commissions/tiers/${tier}`, values, "Tier updated.");

      if (data) {
        setTiers(data.tiers);
      }

      return Boolean(data);
    },
    [request]
  );

  const handleBracketUpdate = useCallback(
    async (id, values) => {
      const data = await request(
        "put",
        `/api/commissions/service-fees/${id}`,
        values,
        "Service fee bracket updated."
      );

      if (data) {
        setServiceFeeBrackets(data.serviceFeeBrackets);
      }

      return Boolean(data);
    },
    [request]
  );

  const handleBracketCreate = useCallback(
    async (values) => {
      const data = await request(
        "post",
        "/api/commissions/service-fees",
        values,
        "Service fee bracket added."
      );

      if (data) {
        setServiceFeeBrackets(data.serviceFeeBrackets);
      }

      return Boolean(data);
    },
    [request]
  );

  const handleBracketDelete = useCallback(
    async (id) => {
      const data = await request(
        "delete",
        `/api/commissions/service-fees/${id}`,
        undefined,
        "Service fee bracket deleted."
      );

      if (data) {
        setServiceFeeBrackets(data.serviceFeeBrackets);
      }

      return Boolean(data);
    },
    [request]
  );

  const handleSalesRepTierUpdate = useCallback(
    async (userId, tier) => {
      const data = await request(
        "put",
        `/api/commissions/sales-reps/${encodeURIComponent(userId)}`,
        { tier },
        "Sales rep tier updated."
      );

      if (data) {
        setSalesReps((reps) =>
          reps.map((rep) => (rep.userId === data.userId ? { ...rep, tier: data.tier } : rep))
        );
      }

      return Boolean(data);
    },
    [request]
  );

  const editingProps = { editingKey, setEditingKey, isSaving };

  return (
    <Fragment>
      <PageHeaderRow
        h1="Commissions"
        pretitle="Settings"
        breadcrumbs={[{ link: "/commissions", label: "Commissions" }]}
      />

      <div className="mt-20 position-relative">
        {isLoading ? (
          <Loader />
        ) : (
          <div className="commissions-grid">
            <div className="commissions-grid-tiers">
              <TiersTable tiers={tiers} onUpdate={handleTierUpdate} {...editingProps} />
            </div>
            <div className="commissions-grid-fees">
              <ServiceFeesTable
                brackets={serviceFeeBrackets}
                onUpdate={handleBracketUpdate}
                onCreate={handleBracketCreate}
                onDelete={handleBracketDelete}
                {...editingProps}
              />
            </div>
            <div className="commissions-grid-reps">
              <SalesRepRatesTable
                salesReps={salesReps}
                tiers={tiers}
                onUpdate={handleSalesRepTierUpdate}
                {...editingProps}
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default CommissionsContent;
